import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  MapPin, 
  Sparkles, 
  Shield, 
  Leaf,
  ArrowRight,
  Users,
  Star,
  Award,
  Home as HomeIcon
} from 'lucide-react'

import BannerCarousel from '../components/banners/BannerCarousel'
import PartnersSection from '../components/partners/PartnersSection'

// Components
import Section from '../components/ui/Section'
import Button from '../components/ui/Button'
import GlassCard from '../components/ui/GlassCard'

const Home = () => {
  const navigate = useNavigate()

  const features = [
    {
      icon: MapPin,
      title: "Inclusion Sociale",
      description: "Les Résidences Massena Inc. appuie l'hypothèse qu'indiquant la déficience intellectuelle peut ne pas être un état permanent.",
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Sparkles,
      title: "Recherche d'Autonomie",
      description: "Nos intervenants de plancher développent quotidiennement des activités avec nos résidents en vue d'accroître leur autonomie fonctionnelle",
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Shield,
      title: "Garde Adaptée et Responsive",
      description: "Les jeunes adultes aux besoins particuliers nécessitent une garde adaptée et personnalisée",
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Leaf,
      title: "Environnement Sécuritaire",
      description: "Un cadre de vie harmonieux qui respecte les principes fondamentaux d'hygiène et sanitaires",
      color: 'from-amber-500 to-orange-500',
    },
  ]

  const stats = [
    {
      icon: Users,
      value: '60+',
      label: 'Résidents',
      color: 'text-blue-600'
    },
    {
      icon: HomeIcon,
      value: '25+',
      label: 'Chambres',
      color: 'text-green-600'
    },
    {
      icon: Star,
      value: '4.9',
      label: 'Note',
      color: 'text-yellow-600'
    },
    {
      icon: Award,
      value: '15+',
      label: 'Années',
      color: 'text-purple-600'
    },
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50"></div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Bienvenue aux Résidences Masséna Inc.
            </motion.h1>
            
            <motion.p 
              className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Nous vous offrons un cadre de vie sécuritaire et harmonieux. Notre priorité est de créer des moments privilégiés pour nos usagers et leurs familles.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Button 
                onClick={() => navigate('/services')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-full hover:shadow-lg transition-all duration-300"
              >
                Découvrir nos services
                <ArrowRight className="ml-2" size={20} />
              </Button>
              
              <Button 
                onClick={() => navigate('/gallery')}
                variant="outline"
                className="border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-full hover:bg-blue-600 hover:text-white transition-all duration-300"
              >
                Prendre un rendez-vous
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <Section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4 ${stat.color}`}>
                  <stat.icon size={24} />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Features Section */}
      <Section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Nos Valeurs
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Au service de l'autonomie et du bien-être
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="h-full p-6 text-center hover:shadow-lg transition-all duration-300">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${feature.color} text-white mb-4`}>
                    <feature.icon size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Banner Carousel */}
      <BannerCarousel />

      {/* Partners Section */}
      <PartnersSection />

      {/* CTA Section */}
      <Section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Prêt à nous rejoindre ?
            </h2>
            <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
              Contactez-nous dès aujourd'hui pour découvrir nos services et prendre rendez-vous.
            </p>
            <Button 
              onClick={() => navigate('/appointment')}
              className="bg-white text-blue-600 px-8 py-3 rounded-full hover:shadow-lg transition-all duration-300"
            >
              Prendre rendez-vous
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </motion.div>
        </div>
      </Section>
    </div>
  )
}

export default Home